/**
 * Meeting-notes document model: the pure, dependency-free half of 會議記錄整理.
 *
 * Three jobs live here, all synchronous and unit-testable:
 *
 *  1. **Billing arithmetic** — `meetingNotesCreditCost`.
 *  2. **Prompt assembly** — `renderTranscriptForPrompt` + `buildNotesPrompt`.
 *  3. **Timecodes** — `parseTimecodeToSeconds`, the one reader of the `mm:ss`
 *     the model writes, and `toStoredTimecodeSeconds`／`STORED_TIMECODE_SECONDS`,
 *     the one rule for what a stored timecode may hold.
 *
 * Document conversion is NOT here. Every path that turns a note into something
 * else — Markdown, stored HTML, an exported file — goes through the ProseMirror
 * document now (`meeting-notes/`, plus `@kiao-voice/ui-kit/meeting-notes-render`
 * for the schema half), so「這份文件可以長什麼樣」is answered once, by the editor
 * schema, instead of once per renderer.
 *
 * ## Timecodes are the link back to the transcript
 *
 * The model emits them as a backticked `mm:ss`, which the API snaps to the start
 * of a real cue and stores as a `timecode` node carrying that start to the
 * millisecond, serialized as
 * `<span class="mn-ts" contenteditable="false" data-t="18.24">00:18</span>`.
 * Clicking it seeks the player to the exact `data-t`; the label is always the
 * whole second it falls in (`formatSecondsAsTimecode` floors). A seek to the
 * floored second would start inside the previous sentence, which is why the
 * value keeps its fraction while the label does not.
 *
 * An edit is saved as the editor's HTML re-rendered through the schema
 * (`normalizeMeetingNotesHtml`), which keeps `data-t` exactly as stored. Markdown
 * (copy, `.md` download, MCP) carries only the label: converting `00:18` back
 * with the API's snap lands on the same 18.24, because the snap maps every
 * whole second to one canonical cue start (the earliest in that second), so
 * re-snapping the floor of a snapped value is a no-op.
 */
import { formatSecondsAsTimecode } from './format-timecode.js'

/** Minutes of transcript covered by one credit. */
export const MINUTES_PER_NOTES_CREDIT = 10
export const MINIMUM_NOTES_CREDITS = 1

/**
 * Credits charged for one 整理 run: one per STARTED 10 minutes of transcript,
 * never less than one.
 *
 * Measured 2026-08-07 against Fireworks deepseek-v4-flash on a 10.3-minute
 * meeting: US$0.00128 (≈NT$0.04) per run, i.e. ≈NT$0.004 per transcript-minute.
 * One credit bills at NT$0.48 (Pro) / NT$0.56 (Plus), so this rate carries a
 * ~90% margin while adding only ~10% on top of the transcription itself.
 *
 * An unknown duration bills the minimum rather than nothing: a run that costs
 * real money must never be free just because metadata is missing.
 */
export function meetingNotesCreditCost(durationSecs: number | null | undefined): number {
  if (typeof durationSecs !== 'number' || !Number.isFinite(durationSecs) || durationSecs <= 0) {
    return MINIMUM_NOTES_CREDITS
  }
  return Math.max(
    MINIMUM_NOTES_CREDITS,
    Math.ceil(durationSecs / (MINUTES_PER_NOTES_CREDIT * 60))
  )
}

/** `mm:ss` / `hh:mm:ss` → seconds. Anything else → null. */
export function parseTimecodeToSeconds(raw: string): number | null {
  const trimmed = raw.trim()
  const match = /^(?:(\d{1,2}):)?([0-5]?\d):([0-5]\d)$/.exec(trimmed)
  if (!match) return null
  const hours = match[1] ? Number(match[1]) : 0
  return hours * 3600 + Number(match[2]) * 60 + Number(match[3])
}

/**
 * What a stored timecode's `data-t` may be: non-negative seconds with at most
 * three decimals (millisecond precision). Whole seconds are the form every
 * timecode written before millisecond storage carries, and stay valid.
 */
export const STORED_TIMECODE_SECONDS = /^\d+(?:\.\d{1,3})?$/

/**
 * A seek target → the value a timecode node stores: rounded to the
 * millisecond, never negative. A non-finite input collapses to 0.
 */
export function toStoredTimecodeSeconds(seconds: number): number {
  if (!Number.isFinite(seconds) || seconds <= 0) return 0
  return Math.round(seconds * 1000) / 1000
}

export interface PromptCue {
  start: number
  end: number
  speaker: string | null
  text: string
}

/**
 * One cue per line, `[mm:ss] Speaker: text`. The bracketed timecode is what the
 * model copies into its output, so the format here and the format the template
 * asks for have to agree.
 */
export function renderTranscriptForPrompt(cues: PromptCue[]): string {
  return cues
    .map((cue) => {
      const stamp = `[${formatSecondsAsTimecode(cue.start)}]`
      const who = cue.speaker?.trim() ? `${cue.speaker.trim()}: ` : ''
      return `${stamp} ${who}${cue.text.trim()}`
    })
    .join('\n')
}

export interface NotesPromptMessage {
  role: 'system' | 'user'
  content: string
}

export interface BuildNotesPromptOptions {
  /** Fixed instructions — identical for every run, so it heads the prefix. */
  wrapper: string
  /** The rendered transcript. Shared across every template for this item. */
  transcript: string
  /** The chosen template's markdown. Varies per run, so it goes LAST. */
  template: string
  /**
   * The recording's filename, when there is one. Carries metadata the audio
   * never states out loud (`20260805 常春藤討論雜誌收費` holds both the date and
   * the subject), so without it the date is honestly but uselessly「未提及」.
   */
  filename?: string
  /** `video_transcription_jobs.id`, used to pin replica routing. */
  itemId?: number
}

export interface NotesPrompt {
  messages: NotesPromptMessage[]
  /**
   * Fireworks routes prompt-cache lookups per replica, so a re-run has to land
   * on the same one to hit. Keyed on the transcript item: every 整理 of the
   * same transcript shares a warm prefix regardless of which template it uses.
   */
  sessionAffinity: string | null
}

/**
 * Assemble the chat messages, static content first.
 *
 * The ordering is load-bearing, not stylistic. Measured 2026-08-07 on
 * deepseek-v4-flash with `x-session-affinity` pinned: re-running a 17.4k-token
 * transcript with a DIFFERENT template hit 11,906 / 17,419 cached input tokens
 * (68%) with the transcript ahead of the template. Put the template first and
 * the shared prefix ends at the system message, so every re-run pays full
 * input price. (Short transcripts — under roughly 10k tokens — fit inside one
 * cache block and show no partial hit either way; they cost pennies regardless.)
 */
export function buildNotesPrompt(options: BuildNotesPromptOptions): NotesPrompt {
  const filename = options.filename?.trim()
  return {
    messages: [
      { role: 'system', content: options.wrapper },
      // Between the wrapper and the transcript: it never changes for a given
      // recording, while the transcript changes on every speaker rename or cue
      // correction. Ordering by volatility is what keeps the cached prefix long.
      // Labelled explicitly, because the whole document rests on「只寫逐字稿裡
      // 真的說過的事」and a filename quoted as speech would break that.
      ...(filename
        ? [
            {
              role: 'user' as const,
              content:
                `<錄音檔名>\n${filename}\n</錄音檔名>\n` +
                '這是檔案名稱，不是會議中說過的話。可以用來推測日期與主題，' +
                '但不要當成與會者的發言引用。',
            },
          ]
        : []),
      { role: 'user', content: `<逐字稿>\n${options.transcript}\n</逐字稿>` },
      { role: 'user', content: `<範本>\n${options.template}\n</範本>` },
    ],
    sessionAffinity: options.itemId === undefined ? null : `kiao-notes-item-${options.itemId}`,
  }
}
